import { User } from '@order-management/types';
import { useCallback, useMemo, useState } from 'react';
import { Element, Icon } from 'react-bulma-components';
import { CgSpinner } from 'react-icons/cg';
import {
  FaCopy,
  FaDownload,
  FaEye,
  FaEyeSlash,
  FaFolder,
  FaFolderOpen,
  FaPencil,
} from 'react-icons/fa6';
import { PiWarningFill } from 'react-icons/pi';
import { useThrottledCallback } from 'use-debounce';
import Conditional from '../../../shared/conditional/conditional';
import useToast from '../../../shared/hooks/toasts';
import If from '../../../shared/if/if';
import IconButton from '../../../ui/icon-button/icon-button';
import InboxItem from '../../../ui/inbox-table/inbox-item/inbox-item';
import { Tooltip } from '../../../ui/tooltip/tooltip';

export type DeviceGroupInboxItemProps = {
  title: string;
  deviceCount: number;
  creator?: User;
  danger?: boolean;
  active: boolean;
  visible?: boolean;
  groupCode?: string;
  onClick: () => void;
  onVisibilityClick?: () => void;
  onEditGroupClick?: () => void;
  onExportDeviceGroupDevicesClick?: () => void;
  disableActions?: boolean;
  showCreator?: boolean;
};

export function DeviceGroupInboxItem({
  title,
  deviceCount,
  creator,
  active,
  visible = true,
  danger = false,
  groupCode,
  onClick,
  onVisibilityClick,
  onEditGroupClick,
  onExportDeviceGroupDevicesClick,
  disableActions = false,
  showCreator = true,
}: DeviceGroupInboxItemProps) {
  const toast = useToast();
  const deviceGroupTitle = useMemo(() => {
    const devicesText = deviceCount === 1 ? 'device' : 'devices';
    return `${title} - ${deviceCount} ${devicesText}`;
  }, [title, deviceCount]);
  const [isExportingData, setIsExportingData] = useState(false);

  const deviceGroupCreatorText = useMemo(() => {
    // If no creator is provided, the device group was created during the work order flow
    let creatorText = 'zSpace';
    if (creator) {
      creatorText = creator.deletedAt ? 'deleted user' : creator.email;
    }
    return `Created by ${creatorText}`;
  }, [creator]);

  const copyGroupCode = useThrottledCallback(() => {
    navigator.clipboard.writeText(groupCode!);
    toast.success('Group code copied to clipboard');
  }, 3000);

  const handleCopyGroupCode = useCallback(
    (e: React.MouseEvent) => {
      if (!groupCode) return;

      e.stopPropagation();
      copyGroupCode();
    },
    [copyGroupCode, groupCode]
  );

  const handleVisibilityClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onVisibilityClick?.();
    },
    [onVisibilityClick]
  );

  const handleEditGroupClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      onEditGroupClick?.();
    },
    [onEditGroupClick]
  );

  const handleDownloadGroupDevicesDataClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();

      setIsExportingData(true);
      try {
        await onExportDeviceGroupDevicesClick?.();
      } finally {
        setIsExportingData(false);
      }
    },
    [onExportDeviceGroupDevicesClick]
  );

  return (
    <InboxItem active={active} onClick={onClick} withControls>
      <Conditional condition={active}>
        <Conditional.True>
          <Element display="flex" flexDirection="column" className="gap-1">
            <Element>
              <Icon color="white">
                <FaFolderOpen />
              </Icon>
              <InboxItem.Label>{deviceGroupTitle}</InboxItem.Label>
              <If condition={!disableActions}>
                <Tooltip message={visible ? 'Hide' : 'Show'}>
                  <IconButton
                    label={visible ? 'Hide device group' : 'Show device group'}
                    color="white"
                    className="ml-2"
                    onClick={handleVisibilityClick}
                  >
                    {visible ? <FaEyeSlash /> : <FaEye />}
                  </IconButton>
                </Tooltip>
                <Tooltip message="Edit">
                  <IconButton
                    label="Edit device group"
                    color="white"
                    className="ml-2"
                    onClick={handleEditGroupClick}
                  >
                    <FaPencil />
                  </IconButton>
                </Tooltip>
                <Tooltip message={isExportingData ? '' : 'Export devices data'}>
                  <IconButton
                    label="Export devices data"
                    color="white"
                    className="ml-2"
                    aria-busy={isExportingData}
                    onClick={handleDownloadGroupDevicesDataClick}
                  >
                    <Conditional condition={isExportingData}>
                      <Conditional.True>
                        <CgSpinner className="animate-spin" />
                      </Conditional.True>
                      <Conditional.False>
                        <FaDownload />
                      </Conditional.False>
                    </Conditional>
                  </IconButton>
                </Tooltip>
              </If>
            </Element>
            <If condition={showCreator}>
              <Element pl={1}>
                <span>{deviceGroupCreatorText}</span>
              </Element>
            </If>
            <If condition={!!groupCode}>
              <Element pl={1}>
                <span>Group code {groupCode}</span>
                <Tooltip message="Copy code">
                  <IconButton
                    label="Copy code"
                    color="white"
                    onClick={handleCopyGroupCode}
                  >
                    <FaCopy />
                  </IconButton>
                </Tooltip>
              </Element>
            </If>
          </Element>
        </Conditional.True>
        <Conditional.False>
          <Icon color={visible ? 'primary-dark' : 'grey-light'}>
            <FaFolder />
          </Icon>
          <InboxItem.Label className={visible ? '' : 'has-text-grey-light'}>
            {deviceGroupTitle}
          </InboxItem.Label>
          <If condition={!disableActions && !visible}>
            <IconButton
              label="Show device group"
              color="grey-light"
              onClick={handleVisibilityClick}
            >
              <FaEye />
            </IconButton>
          </If>
          <If condition={danger}>
            <Icon color="danger-dark">
              <PiWarningFill />
            </Icon>
          </If>
        </Conditional.False>
      </Conditional>
    </InboxItem>
  );
}

export default DeviceGroupInboxItem;
